import {parseDate} from 'common/date';
import type {DateTime} from 'luxon';

export type NetWorthPeriodKey =
  | '1m'
  | '3m'
  | 'ytd'
  | '1y'
  | '3y'
  | '5y'
  | '10y'
  | 'all'
  | 'custom';

export type NetWorthPeriod = {
  key: NetWorthPeriodKey;
  /** The control's label. */
  label: string;
};

/**
 * Asking for all of history: any date before the first observation works,
 * because the history starts at the first observation instead.
 */
export const ALL_TIME_START = '1970-01-01';

export const NET_WORTH_PERIODS: NetWorthPeriod[] = [
  {key: '1m', label: '1M'},
  {key: '3m', label: '3M'},
  {key: 'ytd', label: 'YTD'},
  {key: '1y', label: '1Y'},
  {key: '3y', label: '3Y'},
  {key: '5y', label: '5Y'},
  {key: '10y', label: '10Y'},
  {key: 'all', label: 'All'},
  {key: 'custom', label: 'Custom'},
];

export const DEFAULT_NET_WORTH_PERIOD: NetWorthPeriodKey = '1y';

export const netWorthPeriodByKey = (key: string | null): NetWorthPeriodKey =>
  NET_WORTH_PERIODS.find((period) => period.key === key)?.key ??
  DEFAULT_NET_WORTH_PERIOD;

/**
 * A custom period's start, from the URL's `since` month (YYYY-MM): the first
 * of that month. Null when missing, malformed, or after today — a linked URL
 * can carry any month, and the history ends today.
 */
export const customStart = (since: string | null, today: DateTime): string | null => {
  if (since == null || !/^\d{4}-\d{2}$/.test(since)) return null;
  const date = parseDate(`${since}-01`);
  if (!date.isValid || date > today) return null;
  return date.toISODate();
};

/**
 * The ISO date a period's history starts on. A custom period with no usable
 * month falls back to the default period.
 */
export const periodStart = (
  key: NetWorthPeriodKey,
  today: DateTime,
  since: string | null,
): string => {
  const iso = (date: DateTime) => date.toISODate() ?? '';
  switch (key) {
    case '1m':
      return iso(today.minus({months: 1}));
    case '3m':
      return iso(today.minus({months: 3}));
    case 'ytd':
      return iso(today.startOf('year'));
    case '1y':
      return iso(today.minus({years: 1}));
    case '3y':
      return iso(today.minus({years: 3}));
    case '5y':
      return iso(today.minus({years: 5}));
    case '10y':
      return iso(today.minus({years: 10}));
    case 'all':
      return ALL_TIME_START;
    case 'custom':
      return customStart(since, today) ?? periodStart(DEFAULT_NET_WORTH_PERIOD, today, null);
  }
};

/**
 * How a change over a period reads: "over 12 months", "this year", or, when
 * the history actually starts later than asked (nothing was recorded
 * earlier) or the period is open-ended, the date it starts.
 */
export const periodChangeLabel = (
  key: NetWorthPeriodKey,
  requestedStart: string,
  actualStart: string,
): string => {
  if (key === 'all' || key === 'custom' || actualStart > requestedStart) {
    return `since ${parseDate(actualStart).toFormat('MMM yyyy')}`;
  }
  switch (key) {
    case '1m':
      return 'over 1 month';
    case '3m':
      return 'over 3 months';
    case 'ytd':
      return 'this year';
    case '1y':
      return 'over 12 months';
    case '3y':
      return 'over 3 years';
    case '5y':
      return 'over 5 years';
    case '10y':
      return 'over 10 years';
  }
};
